import { type JSX, useMemo } from 'react';

import { MappableItem, MultipleMappingBase } from 'Components/MultipleMapping/MultipleMappingBase';
import { Text } from 'Components/Text/Text';
import { ThirdPartyService } from 'Models/TPRM';

import styles from './TprmServiceMapping.module.css';

interface TprmServiceMappingItem extends MappableItem {
    thirdPartyName: string;
    serviceName: string;
}

interface TprmServiceMappingListItemProps {
    handleSelected: (value: TprmServiceMappingItem, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    item: TprmServiceMappingItem;

    selected?: boolean;
}

const TprmServiceMappingListItem = (props: TprmServiceMappingListItemProps): JSX.Element => {
    return (
        <div className={`${styles.text} ${props.selected && styles.selected}`} onClick={(e) => props.handleSelected(props.item, e)}>
            <Text variant="Text2" noStyles>
                {props.item.serviceName}
            </Text>
            <Text variant="Text4" noStyles>
                {props.item.thirdPartyName}
            </Text>
        </div>
    );
};

export interface TprmServiceMappingProps {
    currentlyMappedServiceIds: string[];
    handleChange: (serviceIds: string[]) => void;
    tprmServices: ThirdPartyService[];
}

/** Lets the user choose the TPRM third-party services that a UCRM use case relies on. Used (via `ManageUseCaseFormFields`) on the Manage Use Case and UCRM Intake pages. */
export const TprmServiceMapping = (props: TprmServiceMappingProps): JSX.Element => {
    const mappingItems: TprmServiceMappingItem[] = useMemo(
        () =>
            props.tprmServices.map((service) => ({
                id: service.id,
                title: `${service.vendor_name} - ${service.name}`, // Used by the search filter, so that users can search by either name.
                description: service.description,
                serviceName: service.name,
                thirdPartyName: service.vendor_name,
            })),
        [props.tprmServices]
    );

    if (mappingItems.length === 0) {
        return (
            <Text variant="Text3" noStyles>
                No third-party services have been created.
            </Text>
        );
    }

    return <MultipleMappingBase mappingItems={mappingItems} handleChange={props.handleChange} sortMappingItems={(items) => items.sort((a, b) => a.title.localeCompare(b.title))} currentMappedIdentifiers={props.currentlyMappedServiceIds} renderMappingItems={(items, handleSelected, selected) => items.map((item) => <TprmServiceMappingListItem key={item.id} item={item} handleSelected={handleSelected} selected={selected.includes(item)} />)} />;
};
